import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, Field, PasswordInput } from '@shared/ui';
import { checkRules, passwordScore } from '../model/passwordRules';
import { useSubmitNewPasswordMutation, useVerifyResetTokenQuery } from '../model/queries';
import { useResetEmailStore } from '../model/useResetEmailStore';
import { PasswordRulesList } from './PasswordRulesList';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';

const STRENGTH_COLORS = ['var(--color-danger)', 'var(--color-warn)', '#9DCD5A', 'var(--color-success)', 'var(--color-success)'];
const REDIRECT_SECONDS = 3;

type MatchState = 'empty' | 'ok' | 'bad';

const matchClass: Record<MatchState, string> = {
  empty: 'text-muted-2',
  ok: 'text-success',
  bad: 'text-danger',
};

/** Состояние записи истории страницы сброса: токен из письма, убранный из адреса. */
type ResetLinkState = { resetToken?: string };

function tokenFromHistory(state: unknown): string | null {
  const token = (state as ResetLinkState | null)?.resetToken;
  return typeof token === 'string' && token ? token : null;
}

export function ResetPasswordContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Токен из письма — секрет: читаем один раз и сразу убираем из адреса, как подтверждение
  // почты. Иначе «Назад» после ухода со страницы возвращал его в адрес, а оттуда — в аналитику.
  // Сам токен остаётся в history.state этой записи истории: перезагрузка и вкладка, которую
  // восстановил браузер (ушёл в менеджер паролей), снова открывают форму, а не «Ссылка
  // недействительна». history.state не попадает ни в адрес, ни в аналитику.
  const [token] = useState(() => searchParams.get('token') ?? tokenFromHistory(location.state));
  const storedEmail = useResetEmailStore((s) => s.email);
  const clearEmail = useResetEmailStore((s) => s.clear);
  const submit = useSubmitNewPasswordMutation();
  const verify = useVerifyResetTokenQuery(token);

  // Состояния ссылки (upfront-валидация, до показа формы):
  const checking = !!token && verify.isPending;
  const linkInvalid = !token || verify.isError || verify.data?.valid === false;
  const linkValid = verify.data?.valid === true;

  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [done, setDone] = useState(false);
  const [redirectIn, setRedirectIn] = useState(REDIRECT_SECONDS);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const pw1Ref = useRef<HTMLInputElement>(null);

  const score = useMemo(() => passwordScore(pw1), [pw1]);
  const rules = useMemo(() => checkRules(pw1), [pw1]);
  const matchState: MatchState = !pw2 ? 'empty' : pw1 === pw2 ? 'ok' : 'bad';
  const canSubmit =
    !!token && pw1.length >= 8 && pw2.length > 0 && pw1 === pw2 && !submit.isPending;
  // Реальный email из проверки токена; storedEmail — фолбэк, если пришли из того же
  // браузера, где запрашивали сброс. Без фейкового «user@example.com».
  const accountEmail = verify.data?.email ?? storedEmail ?? null;

  const strengthLabels = t('auth.reset.strength.levels', { returnObjects: true }) as string[];
  const labelIdx = Math.max(0, score - 1);
  const strengthLabel = strengthLabels[labelIdx] ?? strengthLabels[0];
  const strengthColor = STRENGTH_COLORS[labelIdx] ?? STRENGTH_COLORS[0];

  useEffect(() => {
    if (!token || !searchParams.has('token')) return;
    const state: ResetLinkState = { resetToken: token };
    setSearchParams({}, { replace: true, state });
  }, [token, searchParams, setSearchParams]);

  useEffect(() => {
    if (linkValid && !done) pw1Ref.current?.focus();
  }, [linkValid, done]);

  useEffect(() => {
    if (!done) return;
    if (redirectIn <= 0) {
      navigate(paths.auth);
      return;
    }
    const id = window.setTimeout(() => setRedirectIn((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [done, redirectIn, navigate]);

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSubmit || !token) return;
    setSubmitError(null);
    submit.mutate(
      { token, password: pw1 },
      {
        onSuccess: () => {
          setDone(true);
          setRedirectIn(REDIRECT_SECONDS);
          clearEmail();
        },
        onError: (err) => {
          const code = (err as { code?: string }).code;
          setSubmitError(t(code === 'EXPIRED_TOKEN' ? 'auth.reset.errors.expired' : 'auth.reset.errors.generic'));
        },
      },
    );
  };

  return (
    <AnimatePresence mode="wait" initial={false}>
      {checking ? (
        <motion.div
          key="checking"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="flex w-full max-w-[380px] flex-col items-start gap-4"
        >
          <span
            aria-hidden
            className="block h-9 w-9 animate-spin rounded-full border-2 border-line border-t-accent"
          />
          <div className="text-[14px] text-muted">{t('auth.reset.verify.checking')}</div>
        </motion.div>
      ) : linkInvalid ? (
        <motion.div
          key="invalid"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="flex w-full max-w-[400px] flex-col gap-[18px]"
        >
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-[20px] bg-danger/15 text-3xl text-danger">
            !
          </div>
          <h1 className="font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-[38px]">
            {t('auth.reset.verify.invalid.title')}
          </h1>
          <div className="max-w-[380px] text-[14px] leading-[1.6] text-muted">
            {t('auth.reset.verify.invalid.body')}
          </div>
          <Link
            to={paths.forgotPassword}
            className="inline-flex w-fit items-center justify-center gap-2 rounded-pill bg-accent px-7 py-4 text-[15px] font-medium tracking-[0.02em] text-fg-inv transition-colors hover:bg-accent-hi"
          >
            <span>{t('auth.reset.verify.invalid.cta')}</span>
            <span aria-hidden className="text-sm">→</span>
          </Link>
          <div className="text-[14px] text-muted">
            {t('auth.reset.backPrefix')}{' '}
            <Link to={paths.auth} className="font-medium text-accent hover:underline">
              {t('auth.reset.backLink')}
            </Link>
          </div>
        </motion.div>
      ) : !done ? (
        <motion.form
          key="form"
          onSubmit={handleSubmit}
          noValidate
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className="flex w-full max-w-[380px] flex-col gap-3.5"
        >
          <h1 className="font-serif text-[30px] font-medium leading-tight tracking-[-0.02em] sm:text-[40px]">
            {t('auth.reset.title')}
          </h1>
          <div className="max-w-[340px] text-[14px] leading-[1.55] text-muted">{t('auth.reset.lead')}</div>
          {accountEmail && (
            <div className="amp-mask ym-hide-content mt-1 inline-flex w-fit items-center gap-1.5 rounded-full border border-line bg-card py-[5px] pl-2 pr-2.5 font-mono text-xs text-fg">
              <span aria-hidden className="block h-[5px] w-[5px] rounded-full bg-accent" />
              {accountEmail}
            </div>
          )}

          <Field
            label={t('auth.reset.pw1Label')}
            hint={
              <span className="font-normal normal-case tracking-[0.02em] text-muted-2">
                {t('auth.reset.pw1Hint')}
              </span>
            }
          >
            <PasswordInput
              ref={pw1Ref}
              autoComplete="new-password"
              placeholder="••••••••"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
            />
            <PasswordStrengthMeter score={score} />
            <div className="mt-2 flex items-center justify-between text-[12px] tracking-[0.02em] text-muted">
              <span>
                {!pw1 ? (
                  t('auth.reset.strength.idle')
                ) : (
                  <>
                    {t('auth.reset.strength.label')} ·{' '}
                    <motion.b
                      key={strengthLabel}
                      initial={{ opacity: 0, y: 2 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                      className="font-medium"
                      style={{ color: strengthColor }}
                    >
                      {strengthLabel}
                    </motion.b>
                  </>
                )}
              </span>
              <span>{t('auth.reset.chars', { n: pw1.length })}</span>
            </div>
            <PasswordRulesList rules={rules} hasInput={pw1.length > 0} />
          </Field>

          <Field label={t('auth.reset.pw2Label')}>
            <PasswordInput
              autoComplete="new-password"
              placeholder="••••••••"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              invalid={matchState === 'bad'}
            />
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={matchState}
                initial={{ opacity: 0, y: 3 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -3 }}
                transition={{ duration: 0.18 }}
                className={cn(
                  'mt-1.5 min-h-[14px] text-[12px] tracking-[0.01em]',
                  matchClass[matchState],
                )}
              >
                {t(`auth.reset.match.${matchState}`)}
              </motion.div>
            </AnimatePresence>
          </Field>

          {submitError && (
            <div className="rounded-xl bg-danger/15 px-3.5 py-2.5 text-[14px] text-danger">{submitError}</div>
          )}

          <Button type="submit" size="lg" disabled={!canSubmit} className="mt-1 w-full">
            <span>{submit.isPending ? '…' : t('auth.reset.submit')}</span>
            <span aria-hidden className="text-sm">→</span>
          </Button>

          <div className="mt-1 text-center text-[14px] text-muted">
            {t('auth.reset.backPrefix')}{' '}
            <Link to={paths.auth} className="font-medium text-accent hover:underline">
              {t('auth.reset.backLink')}
            </Link>
          </div>
        </motion.form>
      ) : (
        <motion.div
          key="done"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.32, ease: 'easeOut' }}
          className="flex w-full max-w-[400px] flex-col gap-[18px]"
        >
          <motion.div
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.55, type: 'spring', stiffness: 240, damping: 14 }}
            className="inline-flex h-16 w-16 items-center justify-center rounded-[20px] bg-success/15 text-3xl text-success"
          >
            ✓
          </motion.div>

          <h1 className="font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-[38px]">
            {t('auth.reset.done.title')}{' '}
            <em className="font-normal italic text-accent">{t('auth.reset.done.titleEm')}</em>.
          </h1>
          <div className="max-w-[380px] text-[14px] leading-[1.6] text-muted">{t('auth.reset.done.sub')}</div>

          <div className="mt-1.5 flex items-center gap-3.5 rounded-2xl border border-line bg-card px-4 py-3.5">
            <span
              aria-hidden
              className="block h-8 w-8 flex-shrink-0 animate-spin rounded-full border-2 border-line border-t-accent"
            />
            <div className="flex-1 text-[14px] leading-[1.5] text-muted">
              {t('auth.reset.done.redirectPrefix')}{' '}
              <b className="font-medium text-fg">{t('auth.reset.done.redirectStrong')}</b>
              {t('auth.reset.done.redirectSuffix')}
            </div>
            <motion.div
              key={redirectIn}
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.25, ease: 'backOut' }}
              className="rounded-lg bg-accent/10 px-2.5 py-[5px] font-mono text-[14px] font-medium tracking-[0.04em] text-accent"
            >
              {Math.max(0, redirectIn)}
            </motion.div>
          </div>

          <Link
            to={paths.auth}
            className="inline-flex items-center justify-center gap-2 rounded-pill bg-accent px-7 py-4 text-[15px] font-medium tracking-[0.02em] text-fg-inv transition-colors hover:bg-accent-hi"
          >
            <span>{t('auth.reset.done.loginNow')}</span>
            <span aria-hidden className="text-sm">→</span>
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
